import type { ExplorationVolume } from '../../../types/exploration';
import {
  fillForBubble,
  HIERARCHY_TYPES,
  isVolumeManaged,
  strokeForBubble,
  strokeOpacityForBubble,
  strokeWidthForBubble,
} from '../../../lib/bubble/bubbleHelpers';
import type { Selection } from 'd3-selection';

import type { PackLayoutNode } from '../../../lib/bubble/packNodeTypes';
import type { CircleNodeSelection } from '../../../lib/d3/d3SelectionTypes';
import { infraBubbleStyles as s, volumeBubbleStyles as vb } from '../bubbleDimColors';
import { setVolumeBubbleShadow } from '../bubbleEmphasis';
import {
  resolveSelectedInstanceId,
  resolveSelectedVolumeId,
  type BubbleRenderOptions,
} from './bubbleRenderOptions';
import { dispatchBubbleClickAction } from './bubbleClickDispatch';
import { showBubbleTooltip } from './bubbleTooltip';

export function bindBubbleCircleEvents(deps: {
  bubbleG: CircleNodeSelection;
  options: BubbleRenderOptions;
  isHaloHierarchy: (d: PackLayoutNode) => boolean;
  applyHierarchyHaloState: (
    d: PackLayoutNode,
    el: Element,
    hovered: boolean,
    k: number
  ) => void;
  hoveredHaloRef: { current: { node: PackLayoutNode; bubbleEl: Element } | null };
  currentZoomK: () => number;
  managedInnerByNode: Map<PackLayoutNode, Element>;
  applyInfraHover?: (node: PackLayoutNode | null, hovered: boolean) => void;
}) {
  const {
    bubbleG,
    options,
    isHaloHierarchy,
    applyHierarchyHaloState,
    hoveredHaloRef,
    currentZoomK,
    managedInnerByNode,
    applyInfraHover,
  } = deps;

  const setInfraHover = applyInfraHover ?? options.applyInfraHover;

  (bubbleG as unknown as Selection<SVGCircleElement, PackLayoutNode, Element, unknown>)
    .on('mouseenter', function (this: SVGCircleElement, e: MouseEvent, d: PackLayoutNode) {
      if (options.getSidebarHighlight?.()) {
        showBubbleTooltip(e, d, options);
        return;
      }
      if (isHaloHierarchy(d)) {
        hoveredHaloRef.current = { node: d, bubbleEl: this };
        applyHierarchyHaloState(d, this, true, currentZoomK());
      } else if (HIERARCHY_TYPES.has(d.data.type) && d.data.type !== 'status') {
        const isSelectedInstance =
          d.data.type === 'instance' && d.data.name === resolveSelectedInstanceId(options);
        if (!isSelectedInstance) {
          this.setAttribute('stroke', s.bubble.border.hover);
        }
        setInfraHover?.(d, true);
      }
      if (d.data.type === 'status' || d.data.type === 'reason') {
        this.setAttribute('stroke', vb.status.hover.mutedBorder);
        this.setAttribute('stroke-opacity', String(vb.status.hover.signalStrokeOpacity));
      }
      if (d.data.type === 'volume') {
        const isSelected = d.data.volume?.ebsId === resolveSelectedVolumeId(options);
        // A persistent selection/highlight glow already owns the filter — hovering
        // over it must not re-save it as the resting baseline.
        if (this.dataset.sbVolumeSelect !== '1' && this.dataset.sbHighlight !== '1') {
          setVolumeBubbleShadow(this, true);
        }
        if (!isSelected) {
          this.setAttribute('stroke', vb.volume.hover.stroke);
        }
      }
      if (d.data._isOverflow) {
        this.setAttribute('stroke', vb.overflow.hoverStroke);
        this.setAttribute('filter', vb.overflow.hoverShadowFilter);
      }
      showBubbleTooltip(e, d, options);
    })
    .on('mouseleave', function (this: SVGCircleElement, _e: MouseEvent, d: PackLayoutNode) {
      if (options.getSidebarHighlight?.()) {
        options.reapplySidebarHighlight?.();
        options.hideTooltip();
        return;
      }
      if (isHaloHierarchy(d)) {
        hoveredHaloRef.current = null;
        applyHierarchyHaloState(d, this, false, currentZoomK());
      } else if (HIERARCHY_TYPES.has(d.data.type) && d.data.type !== 'status') {
        if (d.data.type === 'instance' && d.data.name === resolveSelectedInstanceId(options)) {
          options.hideTooltip();
          options.reapplyInstanceSelection?.();
          return;
        }
        setInfraHover?.(d, false);
        this.setAttribute('stroke', strokeForBubble(d, options.view));
        this.setAttribute('stroke-width', String(strokeWidthForBubble(d)));
      }
      if (d.data.type === 'status' || d.data.type === 'reason') {
        this.setAttribute('stroke', strokeForBubble(d, options.view));
        this.setAttribute('stroke-opacity', String(strokeOpacityForBubble(d)));
        this.setAttribute('stroke-width', String(strokeWidthForBubble(d)));
      }
      if (d.data.type === 'volume') {
        if (d.data.volume?.ebsId === resolveSelectedVolumeId(options)) {
          options.hideTooltip();
          options.reapplyVolumeSelection?.();
          return;
        }
        if (isVolumeManaged(d.data.volume as ExplorationVolume | undefined)) {
          this.setAttribute('stroke', vb.volume.managed.outerStroke);
          this.setAttribute('fill', fillForBubble(d));
          const inner = managedInnerByNode.get(d);
          if (inner) {
            inner.setAttribute('stroke', vb.volume.managed.innerStroke);
            inner.setAttribute('fill', vb.volume.managed.innerFill);
          }
        } else {
          this.setAttribute('stroke', strokeForBubble(d, options.view));
          this.setAttribute('stroke-width', String(strokeWidthForBubble(d)));
        }
        // Mirror the mouseenter guard: without a stateKey this strips the filter
        // outright, which would tear off a persistent selection/highlight glow.
        if (this.dataset.sbHighlight !== '1' && this.dataset.sbVolumeSelect !== '1') {
          setVolumeBubbleShadow(this, false);
        }
      }
      if (d.data._isOverflow) {
        this.setAttribute('stroke', strokeForBubble(d, options.view));
        this.removeAttribute('filter');
      }
      options.hideTooltip();
    })
    .on('click', (e, d: PackLayoutNode) => {
      dispatchBubbleClickAction(e, d, options);
    });
}
